import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import { useRequireSuperAdmin } from '../../lib/useRequireSuperAdmin';
import { api, type SuperWorkspace } from '../../lib/api';
import { relativeTime } from '../../lib/dashboardHelpers';

export default function AdminWorkspacesPage() {
  const gate = useRequireSuperAdmin();
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState<SuperWorkspace[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!gate.ready) return;
    api
      .superWorkspaces()
      .then(setWorkspaces)
      .catch(err => setError((err as Error).message || 'Failed to load workspaces'));
  }, [gate.ready]);

  const filtered = useMemo(() => {
    if (!workspaces) return null;
    if (!filter.trim()) return workspaces;
    const q = filter.toLowerCase();
    return workspaces.filter(
      w =>
        w.domain.toLowerCase().includes(q) ||
        (w.owner_email ?? '').toLowerCase().includes(q),
    );
  }, [workspaces, filter]);

  if (!gate.ready) return null;

  return (
    <AdminShell
      title="Workspaces"
      subtitle="All workspaces in the system — domain-based and personal."
    >
      {error && <ErrorBanner message={error} />}

      <div
        className="bg-surface overflow-hidden"
        style={{
          border: '1px solid var(--color-hairline)',
          borderRadius: 16,
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        <div
          className="flex items-center bg-surface"
          style={{
            gap: 12,
            padding: '14px 20px',
            borderBottom: '1px solid var(--color-hairline)',
          }}
        >
          <input
            placeholder="Filter by domain or owner email…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            className="flex-1 outline-none text-[13px] text-ink min-w-0"
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-hairline)',
              borderRadius: 10,
              background: 'var(--color-bg-alt)',
            }}
          />
          <span className="text-xs text-ink-muted whitespace-nowrap">
            {filtered ? (
              <>
                <b className="font-semibold text-ink-soft">{filtered.length}</b> of{' '}
                {workspaces?.length ?? 0}
              </>
            ) : (
              '…'
            )}
          </span>
        </div>

        <div
          className="grid bg-bg-alt"
          style={{
            gridTemplateColumns: '2fr 100px 1.5fr 80px 80px 90px 110px 130px',
            gap: 12,
            padding: '10px 20px',
            borderBottom: '1px solid var(--color-hairline)',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--color-ink-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          <div>Domain</div>
          <div>Type</div>
          <div>Owner</div>
          <div>Members</div>
          <div>Links</div>
          <div>Clicks</div>
          <div>Last click</div>
          <div>Created</div>
        </div>

        {!filtered ? (
          <div className="text-ink-soft text-sm" style={{ padding: 40, textAlign: 'center' }}>
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-ink-muted text-sm" style={{ padding: 40, textAlign: 'center' }}>
            No workspaces match.
          </div>
        ) : (
          filtered.map(w => (
            <div
              key={w.id}
              onClick={() => navigate(`/admin/workspaces/${w.id}`)}
              className="grid items-center cursor-pointer transition-colors duration-150"
              style={{
                gridTemplateColumns: '2fr 100px 1.5fr 80px 80px 90px 110px 130px',
                gap: 12,
                padding: '12px 20px',
                borderBottom: '1px solid var(--color-hairline)',
                background: 'var(--color-surface)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'var(--color-bg)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'var(--color-surface)';
              }}
            >
              <div className="font-mono text-sm font-semibold text-ink overflow-hidden text-ellipsis whitespace-nowrap">
                {w.domain}
              </div>
              <div>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 6,
                    background: w.is_personal ? 'var(--color-lilac)' : 'var(--color-sky)',
                    color: w.is_personal ? 'var(--color-lilac-deep)' : 'var(--color-sky-deep)',
                  }}
                >
                  {w.is_personal ? 'personal' : 'team'}
                </span>
              </div>
              <div className="text-xs text-ink-soft overflow-hidden text-ellipsis whitespace-nowrap">
                {w.owner_email ?? '—'}
              </div>
              <div className="text-sm tabular-nums text-ink">{w.member_count}</div>
              <div className="text-sm tabular-nums text-ink">{w.link_count}</div>
              <div className="text-sm tabular-nums text-ink">{w.total_clicks.toLocaleString()}</div>
              <div className="text-xs text-ink-muted">
                {w.last_click_at ? relativeTime(w.last_click_at) : '—'}
              </div>
              <div className="text-xs text-ink-muted">{relativeTime(w.created_at)}</div>
            </div>
          ))
        )}
      </div>
    </AdminShell>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: 14,
        marginBottom: 16,
        background: 'var(--color-peach)',
        color: 'var(--color-peach-deep)',
        borderRadius: 12,
        fontSize: 13,
      }}
    >
      {message}
    </div>
  );
}
